/**
 * Phase 7 PR 1 (post-smoke fix) — adapter for `<TeamFeed />` on the
 * real-data path of `/v2/today`.
 *
 * Maps `KudoFeedRow[]` (from `useTeamKudos`) to the `KudosEntry[]`
 * shape `<TeamFeed />` consumes. Pure — no React, no I/O — so it can
 * be unit-tested in isolation.
 *
 * Smoke 2026-05-15 surfaced waisports seeing a Lida/Marco kudos card
 * driven entirely by the `ramiroTeam` fixture. The first fix (commit
 * 8e5163e) hid the section on the real-data path entirely; this
 * follow-up wires real kudos so the section is shown when entries
 * exist and hidden when empty (TeamFeed itself returns null on
 * `entries.length === 0` — see TeamFeed.tsx:17).
 *
 * Vanilla parity: vanilla's kudos feed on the dashboard renders the
 * same data shape (`fromName`, kudo-type emoji + label, `timeAgo`,
 * optional message). No new fields are introduced here.
 */

import {
  KUDO_TYPES,
  lookupKudoType,
  type KudoTypeKey,
} from './kudoTypes';
import type { KudoFeedRow } from '../hooks/useTeamKudos';
import type { KudosEntry } from '../fixtures/team';

/**
 * Coach-token palette for the avatar circle. Stable per-name (hashed)
 * so the same teammate gets the same color across reloads. The fixture
 * uses `var(--c-training)` and `var(--c-mental)` — we extend to the
 * full coach palette so teams of 3+ don't visually collide.
 */
const AVATAR_BG_PALETTE: readonly string[] = [
  'var(--c-training)', // teal
  'var(--c-mental)', // plum
  'var(--c-recovery)', // emerald
  'var(--c-race)', // race blue
  'var(--c-gear)', // bronze
];

/**
 * Deterministic per-name color picker. `name.charCodeAt(0)` is enough
 * for the common case (3-10 teammates) — we don't need cryptographic
 * distribution, just visual stability across reloads.
 *
 * Exported for tests.
 */
export function pickAvatarBg(name: string): string {
  if (!name) return AVATAR_BG_PALETTE[0]!;
  // Sum char codes to spread color across the alphabet — picking by
  // just `name[0]` would cluster all "A" / "M" names into one color.
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return AVATAR_BG_PALETTE[sum % AVATAR_BG_PALETTE.length]!;
}

/**
 * Format an ISO timestamp as a "Nh / Nd / Mmm D" relative-time string
 * matching the vanilla feed (and the fixture's `'2h'` / `'4h'` shape).
 *
 *   < 60 sec   → "now"
 *   < 60 min   → "Nm"
 *   < 24 hr    → "Nh"
 *   < 7 days   → "Nd"
 *   ≥ 7 days   → "Mmm D" (e.g. "May 8")
 *
 * A kudo someone ELSE sent carries only the DATE (`YYYY-MM-DD`, B299), read
 * day-level from the viewer's today: "Today", "Yesterday", "Nd ago" under 7
 * days, then "Mmm D". It is never parsed as an instant (a UTC midnight would
 * print an hour count up to a day off).
 *
 * Exported for tests.
 */
export function formatTimeAgo(iso: string, now: Date = new Date()): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) {
    const [y, m, d] = [Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3])];
    const dayCount = Math.round(
      (Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) - Date.UTC(y, m - 1, d)) /
        86_400_000,
    );
    if (dayCount <= 0) return 'Today';
    if (dayCount === 1) return 'Yesterday';
    if (dayCount < 7) return `${dayCount}d ago`;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthNames[m - 1] ? `${monthNames[m - 1]} ${d}` : '';
  }
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const deltaMs = now.getTime() - then.getTime();
  if (deltaMs < 60_000) return 'now';
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[then.getMonth()]} ${then.getDate()}`;
}

/**
 * Compose the action phrase from a kudo type — matches the vanilla
 * copy convention "sent <label>" (e.g. "sent Great Run", "sent Beast
 * Mode"). Falls back to "sent kudos" for unknown enum values
 * (defensive — `isKudoTypeKey` filters in the hook, so this should
 * never fire for real rows).
 */
function actionFromType(kudoType: KudoTypeKey): string {
  const def = lookupKudoType(kudoType);
  return def ? `sent ${def.label}` : 'sent kudos';
}

/**
 * Map ONE row. Pure. Exported for tests.
 */
export function kudoRowToEntry(
  row: KudoFeedRow,
  now: Date = new Date(),
): KudosEntry {
  const name = row.fromName.trim() || 'Teammate';
  const initial = name.charAt(0).toUpperCase();
  const def = lookupKudoType(row.kudoType);
  const message = row.message.trim();
  return {
    id: row.id,
    name,
    initial,
    avatarBg: pickAvatarBg(name),
    action: actionFromType(row.kudoType),
    payload: message ? `"${message}"` : undefined,
    timeAgo: formatTimeAgo(row.createdAt, now),
    emoji: def?.emoji,
  };
}

/**
 * Map the array. The hook already filters out unknown kudo types and
 * sorts DESC by createdAt server-side — we just project each row.
 *
 * Empty input → empty output → `<TeamFeed entries={[]}>` returns
 * `null` (see TeamFeed.tsx:17) so the section hides cleanly.
 */
export function kudoRowsToEntries(
  rows: KudoFeedRow[],
  now: Date = new Date(),
): KudosEntry[] {
  return rows.map((r) => kudoRowToEntry(r, now));
}

/** Re-export for tests that want to exercise the palette. */
export const __testing__ = { AVATAR_BG_PALETTE, KUDO_TYPES };
